import type { WebGLInfo } from "three";
import { create } from "zustand";
import { combine } from "zustand/middleware";

const createInitialMetricsState = () => ({
  // Metrics state
  fps: 0,
  maxFrameTime: 0,
  triangles: 0,
  drawCalls: 0,
  geometries: 0,
  textures: 0,
  programs: 0,
  memoryUsed: 0,

  // FPS calculation helpers (not exposed to components)
  _frameCount: 0,
  _lastTime: performance.now(),
  _lastFrameTime: performance.now(),
  _maxFrameTimeSinceReset: 0,
});

const useMetricsStore = create(
  combine(
    createInitialMetricsState(),

    (set, get) => ({
      actions: {
        updateMetrics: (rendererInfo: WebGLInfo) => {
          const state = get();
          const now = performance.now();
          const deltaTime = now - state._lastTime;

          // Calculate FPS directly from frames counted since last update
          const fps = deltaTime > 0 ? Math.round((state._frameCount * 1000) / deltaTime) : 0;

          // Get max frame time since last reset and reset it
          const maxFrameTime = Math.round(state._maxFrameTimeSinceReset);

          // Get memory usage (if available)
          let memoryUsed = 0;
          const perf = performance as Performance & {
            memory?: { usedJSHeapSize: number };
          };
          if (perf.memory) {
            memoryUsed = Math.round(perf.memory.usedJSHeapSize / (1024 * 1024));
          }

          set({
            fps,
            maxFrameTime,
            triangles: rendererInfo.render.triangles || 0,
            drawCalls: rendererInfo.render.calls,
            geometries: rendererInfo.memory.geometries,
            textures: rendererInfo.memory.textures,
            programs: rendererInfo.programs?.length || 0,
            memoryUsed,
            _frameCount: 0,
            _lastTime: now,
            _maxFrameTimeSinceReset: 0, // Reset max frame time after displaying
          });
        },

        // Call this every frame to count frames and track frame times
        incrementFrameCount: () => {
          const state = get();
          const now = performance.now();
          const frameTime = now - state._lastFrameTime;

          // Track max frame time since last reset
          const newMaxFrameTime = Math.max(state._maxFrameTimeSinceReset, frameTime);

          set({
            _frameCount: state._frameCount + 1,
            _lastFrameTime: now,
            _maxFrameTimeSinceReset: newMaxFrameTime,
          });
        },

        // Reset metrics
        resetMetrics: () =>
          set({
            fps: 0,
            maxFrameTime: 0,
            triangles: 0,
            drawCalls: 0,
            geometries: 0,
            textures: 0,
            programs: 0,
            memoryUsed: 0,
            _frameCount: 0,
            _lastTime: performance.now(),
            _lastFrameTime: performance.now(),
            _maxFrameTimeSinceReset: 0,
          }),
      },
    })
  )
);

// Custom hooks for individual metrics
export const useFps = () => useMetricsStore((state) => state.fps);
export const useMaxFrameTime = () => useMetricsStore((state) => state.maxFrameTime);
export const useTriangles = () => useMetricsStore((state) => state.triangles);
export const useDrawCalls = () => useMetricsStore((state) => state.drawCalls);
export const useGeometries = () => useMetricsStore((state) => state.geometries);
export const useTextures = () => useMetricsStore((state) => state.textures);
export const usePrograms = () => useMetricsStore((state) => state.programs);
export const useMemoryUsed = () => useMetricsStore((state) => state.memoryUsed);

// Actions hook (doesn't cause re-renders)
export const useMetricsActions = () => useMetricsStore((state) => state.actions);
